import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject, merge, of, concat } from 'rxjs';
import { scan, switchMap, map, catchError, shareReplay } from 'rxjs/operators';
import { ProductsService } from '../Services/products.service';
import { JoinOrDashPipe } from '../pipes/join-or-dash.pipe';
import { FormatSubstancesPipe } from '../pipes/format-substances.pipe';
import { CatalogLabelPipe } from '../pipes/catalog-label.pipe';
import { CatalogClassPipe } from '../pipes/catalog-class.pipe';
import { VisiblePagesPipe } from '../pipes/visible-pages.pipe';
import { PageRangePipe } from '../pipes/page-range.pipe';
import {
  ProductsResultsState,
  ProductsFetchAction,
  createInitialResultsState,
  isFetchAction,
  buildSearchRequest,
  applyFetchContext,
} from './products.models';

const PRODUCT_PIPES = [
  JoinOrDashPipe,
  FormatSubstancesPipe,
  CatalogLabelPipe,
  CatalogClassPipe,
  VisiblePagesPipe,
  PageRangePipe,
] as const;

@Component({
  selector: 'app-products',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ...PRODUCT_PIPES],
  templateUrl: './products.component.html',
  styleUrl: './products.component.css',
})
export class ProductsComponent {
  private readonly productsService = inject(ProductsService);
  private readonly formBuilder = inject(FormBuilder);

  private readonly fetch$ = new Subject<ProductsFetchAction>();

  readonly filterForm = this.formBuilder.nonNullable.group({
    drugName: '',
    manufacturer: '',
    catalogUk: true,
    catalogIrish: true,
  });

  readonly results$: Observable<ProductsResultsState> = merge(
    of(createInitialResultsState()),
    this.fetch$.pipe(
      scan(applyFetchContext, {
        action: null,
        state: createInitialResultsState(),
      }),
      switchMap(({ action, state }) => {
        if (!isFetchAction(action)) {
          return of(state);
        }
        const request = buildSearchRequest(
          this.filterForm.getRawValue(),
          state.pageNumber,
          state.pageSize,
        );
        return concat(
          of({ ...state, loading: true }),
          this.productsService.searchProducts(request).pipe(
            map((page) => ({
              ...state,
              loading: false,
              errorMessage: undefined,
              products: page.data,
              pageNumber: page.pageNumber,
              totalPages: page.totalPages,
              totalRecords: page.totalRecords,
            })),
            catchError(() =>
              of({
                ...state,
                loading: false,
                products: [],
                totalPages: 0,
                totalRecords: 0,
                errorMessage: 'Failed to load products',
              }),
            ),
          ),
        );
      }),
    ),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  search(): void {
    this.fetch$.next({ type: 'search' });
  }

  previousPage(state: ProductsResultsState): void {
    if (state.pageNumber > 1) {
      this.goToPage(state.pageNumber - 1);
    }
  }

  nextPage(state: ProductsResultsState): void {
    if (state.pageNumber < state.totalPages) {
      this.goToPage(state.pageNumber + 1);
    }
  }

  goToPage(pageNumber: number): void {
    this.fetch$.next({ type: 'page', pageNumber });
  }
}
